import { useMemo } from 'react';

import { GMetrixTreeNode, GMetrixTreeView, TreeNodeId } from 'components/GMetrixTreeView/GMetrixTreeView';
import { NumericInput } from 'pages/licenses/combo_license/components/NumericInput';
import { DeliverablePayload } from 'apis/LicenseApi';
import { useIntl } from 'react-intl';

const OTHER_BUCKET_KEY = '__other__';

type DeliverableTabProps<T extends { Id: number; Name: string }> = {
    contentLabel: string;
    deliverable: DeliverablePayload;
    getGroupKey: (item: T) => string;
    includeOtherBucket?: boolean;
    items: T[];
    onChange: (next: DeliverablePayload) => void;
    showSeats: boolean;
};

const buildNodes = <T extends { Id: number; Name: string }>(
    items: T[],
    getGroupKey: (item: T) => string,
    includeOtherBucket: boolean,
    otherLabel: string,
    uncategorizedLabel: string
): GMetrixTreeNode[] => {

    const groups = new Map<string, { label: string; nodes: GMetrixTreeNode[] }>();

    items.forEach(item => {

        const rawKey = getGroupKey(item);
        const key = rawKey || (includeOtherBucket ? OTHER_BUCKET_KEY : uncategorizedLabel);
        const label = key === OTHER_BUCKET_KEY ? otherLabel : (rawKey || uncategorizedLabel);

        let bucket = groups.get(key);
        if (!bucket) {

            bucket = { label, nodes: [] };
            groups.set(key, bucket);

        }
        bucket.nodes.push({ id: item.Id, label: item.Name });

    });

    return Array.from(groups.entries()).map(([key, group]) => ({
        id: `group_${key}`,
        children: group.nodes,
        label: group.label
    }));

};

export const DeliverableTab = <T extends { Id: number; Name: string }>(props: DeliverableTabProps<T>) => {

    const { contentLabel, deliverable, getGroupKey, includeOtherBucket = false, items, onChange, showSeats } = props;

    const { formatMessage } = useIntl();

    const otherLabel = formatMessage({ id: 'other.5e0d2241', defaultMessage: 'Other' });
    const uncategorizedLabel = formatMessage({ id: 'uncategorized.d429a685', defaultMessage: 'Uncategorized' });

    const nodes = useMemo(
        () => buildNodes(items, getGroupKey, includeOtherBucket, otherLabel, uncategorizedLabel),
        [items, getGroupKey, includeOtherBucket, otherLabel, uncategorizedLabel]
    );

    const handleSelectionChange = (ids: TreeNodeId[]) => {

        const numericIds = ids
            .map(id => Number(id))
            .filter(n => !Number.isNaN(n));
        onChange({ ...deliverable, contentIds: numericIds });

    };

    return (
        <div className="pt-3">
            <div className="row mb-3">
                <div className="col-md-3">
                    <NumericInput
                        label={formatMessage({ id: 'content_per_code.613404ab', defaultMessage: '{content} per code' }, { content: contentLabel })}
                        onChange={v => onChange({ ...deliverable, usesPerCode: v })}
                        value={deliverable.usesPerCode}
                    />
                </div>
                <div className="col-md-3">
                    <NumericInput
                        label={formatMessage({ id: 'max_uses.f21748ab', defaultMessage: 'Max uses' })}
                        onChange={v => onChange({ ...deliverable, maxUses: v })}
                        value={deliverable.maxUses}
                    />
                </div>
                {showSeats ? (
                    <>
                        <div className="col-md-3">
                            <NumericInput
                                label={formatMessage({ id: 'seats.e0bcdf2b', defaultMessage: 'Seats' })}
                                onChange={v => onChange({ ...deliverable, seats: v })}
                                value={deliverable.seats}
                            />
                        </div>
                        <div className="col-md-3">
                            <NumericInput
                                label={formatMessage({ id: 'max_users_per_seat.4f116e74', defaultMessage: 'Max users per seat' })}
                                onChange={v => onChange({ ...deliverable, maxSeatsPerUser: v })}
                                value={deliverable.maxSeatsPerUser}
                            />
                        </div>
                    </>
                ) : null}
            </div>

            <h6 className="fw-bold mb-2">
                {`Select ${contentLabel}`}
            </h6>
            <GMetrixTreeView
                showSearch
                showSelectAll
                defaultExpandedIds={[]}
                nodes={nodes}
                onSelectionChange={handleSelectionChange}
                selectedIds={deliverable.contentIds}
            />
        </div>
    );

};
